import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  MapPinned,
  Plus,
  PlusCircle,
  RotateCw,
  Route,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/hooks/useAuth";
import freightService from "@/services/freightService";
import { showError, showInfo } from "@/store/errorStore";
import FreightCalculator from "./FreightCalculator";
import FreightRateCard from "./FreightRateCard";
import FreightRateFormModal from "./FreightRateFormModal";

/**
 * Erros HTTP já são exibidos pelo modal global (axiosInterceptor);
 * só mostramos aqui erros sem resposta do servidor (ex.: falha local).
 */
const showLocalError = (error, fallback) => {
  if (!error?.response && !error?.request) {
    showError(error?.message || fallback, "Frete");
  }
};

/** "R$ 2 a 10" ou "R$ 2,50 a 10,00" (com centavos só quando houver) */
const formatTariffRange = (min, max) => {
  const hasCents = [min, max].some((v) => !Number.isInteger(v));
  const fmt = (v) =>
    v.toLocaleString("pt-BR", {
      minimumFractionDigits: hasCents ? 2 : 0,
      maximumFractionDigits: 2,
    });
  return min === max ? `R$ ${fmt(min)}` : `R$ ${fmt(min)} a ${fmt(max)}`;
};

function MetricCard({ label, children }) {
  return (
    <div className="flex min-w-0 flex-col justify-between rounded-xl border bg-card p-3 shadow-soft md:p-4">
      <span className="truncate text-[11px] font-semibold text-muted-foreground md:text-xs">
        {label}
      </span>
      <div className="mt-1 min-w-0">{children}</div>
    </div>
  );
}

function RatesSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-2.5 md:grid-cols-2 xl:grid-cols-3" aria-hidden="true">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="space-y-3 rounded-xl border bg-card p-3.5">
          <div className="flex items-start justify-between">
            <div className="space-y-2">
              <Skeleton className="h-5 w-32" />
              <Skeleton className="h-3 w-44" />
            </div>
            <Skeleton className="h-8 w-16" />
          </div>
          <Skeleton className="h-11 rounded-lg" />
        </div>
      ))}
    </div>
  );
}

/**
 * Frete e Entregas (docs/tela/code.html): consulta por CEP, métricas e
 * CRUD das faixas de cobrança por raio de entrega.
 */
export default function FreightManagement() {
  const { companyName } = useAuth();

  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingRate, setEditingRate] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loadingRateId, setLoadingRateId] = useState(null);
  const [deletingRate, setDeletingRate] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  /**
   * Carregar taxas ativas
   */
  const loadRates = useCallback(async () => {
    setLoading(true);
    try {
      const data = await freightService.getAllRates();
      setRates(Array.isArray(data) ? data : []);
      setLoadError(false);
    } catch (error) {
      showLocalError(error, "Erro ao carregar taxas de frete");
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRates();
  }, [loadRates]);

  const handleCreate = () => {
    setEditingRate(null);
    setShowModal(true);
  };

  /**
   * Abrir modal de edição (busca dados atualizados por ID)
   */
  const handleEdit = async (rate) => {
    setLoadingRateId(rate.id);
    try {
      const fresh = await freightService.getRateById(rate.id);
      setEditingRate(fresh || rate);
      setShowModal(true);
    } catch (error) {
      showLocalError(error, "Erro ao carregar taxa de frete");
    } finally {
      setLoadingRateId(null);
    }
  };

  const handleSave = async (rateData) => {
    setSaving(true);
    try {
      const range = freightService.formatRange(rateData);
      if (editingRate) {
        await freightService.updateRate(editingRate.id, rateData);
        showInfo(`Taxa de ${range} atualizada com sucesso`, "Taxa Atualizada");
      } else {
        await freightService.createRate(rateData);
        showInfo(`Taxa de ${range} criada com sucesso`, "Taxa Criada");
      }
      setShowModal(false);
      await loadRates();
    } catch (error) {
      showLocalError(error, "Erro ao salvar taxa de frete");
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    const rate = deletingRate;
    if (!rate) return;

    setDeletingRate(null);
    setDeletingId(rate.id);
    try {
      await freightService.deleteRate(rate.id);
      showInfo(
        `Taxa de ${freightService.formatRange(rate)} removida com sucesso`,
        "Taxa Removida",
      );
      await loadRates();
    } catch (error) {
      showLocalError(error, "Erro ao remover taxa de frete");
    } finally {
      setDeletingId(null);
    }
  };

  const summary = useMemo(() => freightService.getSummary(rates), [rates]);
  const gaps = useMemo(() => freightService.findGaps(rates), [rates]);
  const overlapsById = useMemo(
    () =>
      Object.fromEntries(
        rates.map((rate) => [rate.id, freightService.findOverlaps(rate, rates)]),
      ),
    [rates],
  );

  const initialLoading = loading && rates.length === 0;
  const hasRates = summary.count > 0;

  return (
    <div className="mx-auto w-full max-w-6xl space-y-4">
      {/* Título e ação principal */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <span className="font-display text-[13px] font-bold uppercase tracking-[0.12em] text-secondary-foreground">
            Logística • Delivery
          </span>
          <h1 className="font-display text-[22px] font-semibold leading-7 text-foreground md:text-2xl">
            Frete e Entregas
          </h1>
          <p className="mt-0.5 text-xs text-muted-foreground md:text-sm">
            Pesquise frete por CEP e gerencie faixas de cobrança por raio de
            entrega.
          </p>
        </div>
        <button
          type="button"
          onClick={handleCreate}
          className="flex h-10 flex-shrink-0 items-center gap-1.5 rounded-full bg-primary px-4 text-[13px] font-semibold text-primary-foreground shadow-sm transition-all hover:bg-brand-dark active:scale-95"
        >
          <Plus className="h-4 w-4" />
          Nova Taxa
        </button>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] lg:items-start">
        {/* Consulta por CEP */}
        <FreightCalculator />

        {/* Métricas */}
        {initialLoading ? (
          <div className="grid grid-cols-3 gap-2" aria-hidden="true">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-[84px] rounded-xl" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-2">
            <MetricCard label="Faixas ativas">
              <span className="flex items-baseline gap-1">
                <span className="font-display text-[22px] font-bold leading-7 text-foreground">
                  {summary.count}
                </span>
                <span className="text-[11px] font-semibold text-muted-foreground">
                  {summary.count === 1 ? "zona" : "zonas"}
                </span>
              </span>
            </MetricCard>
            <MetricCard label="Cobertura até">
              {hasRates ? (
                <span className="flex items-baseline gap-1 text-primary">
                  <span className="font-display text-[22px] font-bold leading-7">
                    {freightService.formatKm(summary.coverage)}
                  </span>
                  <span className="text-[11px] font-semibold">km</span>
                </span>
              ) : (
                <span className="font-display text-[22px] font-bold leading-7 text-muted-foreground">
                  -
                </span>
              )}
            </MetricCard>
            <MetricCard label="Tarifas">
              {hasRates ? (
                <>
                  <span className="block truncate text-[13px] font-bold text-foreground">
                    {formatTariffRange(summary.min, summary.max)}
                  </span>
                  <span className="block truncate text-[11px] font-semibold text-muted-foreground">
                    {summary.trend}
                  </span>
                </>
              ) : (
                <span className="font-display text-[22px] font-bold leading-7 text-muted-foreground">
                  -
                </span>
              )}
            </MetricCard>
          </div>
        )}
      </div>

      {/* Faixas de distância */}
      <section className="space-y-2.5">
        <div className="flex items-center justify-between gap-3 pt-1">
          <div className="flex items-center gap-2">
            <MapPinned className="h-5 w-5 flex-shrink-0 text-primary" />
            <h3 className="font-display text-lg font-semibold text-foreground">
              Faixas de Distância
            </h3>
          </div>
          <span className="min-w-0 truncate text-right text-[11px] font-semibold text-muted-foreground">
            {companyName ? `Base: ${companyName} • ` : ""}Raio linear
          </span>
        </div>

        {!loading && gaps.length > 0 && (
          <div className="flex items-start gap-2 rounded-lg bg-terra-soft p-3 text-xs text-terra">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <span>
              Distâncias sem taxa cadastrada:{" "}
              <strong>
                {gaps
                  .map((gap) =>
                    freightService.formatRange({
                      minDistanceKm: gap.from,
                      maxDistanceKm: gap.to,
                    }),
                  )
                  .join(", ")}
              </strong>
              . Dentro da área de atendimento, o frete fica indisponível para
              essas distâncias.
            </span>
          </div>
        )}

        {initialLoading ? (
          <RatesSkeleton />
        ) : loadError && !hasRates ? (
          <div className="flex flex-col items-center rounded-xl border bg-card px-6 py-10 text-center shadow-soft">
            <AlertTriangle className="mb-3 h-10 w-10 text-terra" />
            <p className="font-semibold text-foreground">
              Não foi possível carregar as faixas de frete
            </p>
            <button
              type="button"
              onClick={loadRates}
              className="mt-4 flex h-10 items-center gap-1.5 rounded-full bg-surface-container px-5 text-[13px] font-semibold text-primary transition-colors hover:bg-surface-high"
            >
              <RotateCw className="h-4 w-4" />
              Tentar novamente
            </button>
          </div>
        ) : !hasRates ? (
          <div className="flex flex-col items-center rounded-xl border bg-card px-6 py-10 text-center shadow-soft">
            <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-brand-pistachio/30 text-primary">
              <Route className="h-6 w-6" />
            </div>
            <p className="font-semibold text-foreground">
              Nenhuma faixa de frete cadastrada
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              Sem faixas, o cálculo do frete fica indisponível para todos os CEPs.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-2.5 md:grid-cols-2 xl:grid-cols-3">
            {rates.map((rate) => (
              <FreightRateCard
                key={rate.id}
                rate={rate}
                overlaps={overlapsById[rate.id]}
                loadingEdit={loadingRateId === rate.id}
                deleting={deletingId === rate.id}
                onEdit={handleEdit}
                onDelete={setDeletingRate}
              />
            ))}
          </div>
        )}

        {!initialLoading && !(loadError && !hasRates) && (
          <button
            type="button"
            onClick={handleCreate}
            className="flex w-full items-center justify-center gap-2 rounded-full border bg-card py-3.5 text-[13px] font-semibold text-primary shadow-soft transition-all hover:shadow-ambient active:bg-surface-container"
          >
            <PlusCircle className="h-5 w-5" />
            Adicionar nova faixa de distância
          </button>
        )}
      </section>

      <FreightRateFormModal
        open={showModal}
        onOpenChange={setShowModal}
        rate={editingRate}
        rates={rates}
        onSave={handleSave}
        loading={saving}
      />

      <AlertDialog
        open={!!deletingRate}
        onOpenChange={(open) => !open && setDeletingRate(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir faixa de frete</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir a taxa de{" "}
              <strong>{deletingRate && freightService.formatRange(deletingRate)}</strong>{" "}
              ({deletingRate && freightService.formatCurrency(deletingRate.freightValue)})?
              Ela deixará de ser usada no cálculo do frete.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
